import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "gebaeudeunterhalt", label: "Gebäudeunterhalt" },
  { value: "unterhaltsreinigung", label: "Unterhaltsreinigung" },
  { value: "winterservice", label: "Winterservice" },
  { value: "spezialreinigung", label: "Spezialreinigung (Umzug / Bauende / Grund / Fassade)" },
  { value: "gartenarbeiten", label: "Gartenarbeiten" },
  { value: "entsorgung", label: "Entsorgung" },
  { value: "sonstiges", label: "Sonstiges" },
];

const inputCls = "w-full rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-sm backdrop-blur focus:border-[#3B62A5] focus:outline-none transition-colors duration-200";
const labelCls = "font-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-2";

export default function Kontakt() {
  const [params] = useSearchParams();
  usePageMeta({
    title: "Kontakt & Offerte | HRG Service GmbH Mühlau AG",
    description: "Kontaktieren Sie die HRG Service GmbH in Mühlau: unverbindliche Offerte für Gebäudeunterhalt, Reinigung, Winterservice, Gartenarbeiten und Entsorgung. Antwort innert 24 Stunden.",
    path: "/kontakt",
  });
  const SUBSERVICE_MAP = { umzugsreinigung: "spezialreinigung", grundreinigung: "spezialreinigung", bauendreinigung: "spezialreinigung" };
  const rawService = params.get("service") || "sonstiges";
  const preService = SERVICE_OPTIONS.some((s) => s.value === rawService) ? rawService : (SUBSERVICE_MAP[rawService] || "sonstiges");
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: preService, message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde gesendet — wir melden uns innert 24 Stunden.");
      setForm({ name: "", email: "", phone: "", service: "sonstiges", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-20">
      <section className="bg-blueprint border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
          <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#3B62A5] font-semibold mb-6 flex items-center gap-3">
            <span className="inline-block h-[2px] w-10 bg-[#3B62A5]" /> Kontakt
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A111E] leading-[1.08]">
            <MaskedLines lines={[<>Sprechen wir über</>, <span className="text-[#3B62A5]">Ihr Objekt.</span>]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Unverbindliche Offerte, Besichtigung vor Ort oder eine kurze Frage — wir antworten innert 24 Stunden.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <Reveal>
              <div className="glass-card rounded-3xl p-8">
                <h2 className="text-xl font-semibold tracking-tight text-[#0A111E] mb-6">HRG Service GmbH</h2>
                <ul className="space-y-5 text-sm">
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#E8F0FE]"><MapPin className="h-5 w-5 text-[#3B62A5]" /></span>
                    <span className="text-slate-700 leading-relaxed">Rüstenschwilerstrasse 2<br />5642 Mühlau, Schweiz</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#E8F0FE]"><Phone className="h-5 w-5 text-[#3B62A5]" /></span>
                    <a href="tel:+41782238087" className="text-slate-700 hover:text-[#3B62A5] transition-colors duration-200" data-testid="contact-phone-link">+41 78 223 80 87</a>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#E8F0FE]"><Mail className="h-5 w-5 text-[#3B62A5]" /></span>
                    <a href="mailto:info@hrgservice.ch" className="text-slate-700 hover:text-[#3B62A5] transition-colors duration-200" data-testid="contact-email-link">info@hrgservice.ch</a>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#E8F0FE]"><Clock className="h-5 w-5 text-[#3B62A5]" /></span>
                    <span className="text-slate-700">Mo–Fr 07:00–18:00<br />Sa nach Vereinbarung</span>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-white/20 bg-gradient-to-br from-[#3B62A5] to-[#2A477A] p-8 text-white shadow-[0_20px_50px_rgba(59,98,165,0.25)]">
                <p className="font-tech text-xs uppercase tracking-[0.25em] text-white/70 mb-3">24/7 Notfallnummer</p>
                <p className="text-lg font-semibold leading-snug">Notfall? Wir sind rund um die Uhr für Sie erreichbar — 24 Stunden, 7 Tage die Woche.</p>
                <a href="tel:+41765137667" className="mt-4 inline-flex items-center gap-2 bg-white px-5 py-3 text-sm font-semibold text-[#3B62A5] hover:bg-[#E8F0FE] transition-colors duration-200" data-testid="contact-emergency-call">
                  <Phone className="h-4 w-4" /> +41 76 513 76 67
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} className="glass-card rounded-3xl p-8 sm:p-10" data-testid="contact-form">
              <h2 className="text-xl font-semibold tracking-tight text-[#0A111E] mb-8">Offerte anfragen</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className={labelCls}>Name *</label>
                  <input id="contact-name" data-testid="contact-form-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Ihr Name" />
                </div>
                <div>
                  <label htmlFor="contact-email" className={labelCls}>E-Mail *</label>
                  <input id="contact-email" data-testid="contact-form-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="ihre@email.ch" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className={labelCls}>Telefon</label>
                  <input id="contact-phone" data-testid="contact-form-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+41 ..." />
                </div>
                <div>
                  <label htmlFor="contact-service" className={labelCls}>Dienstleistung</label>
                  <select id="contact-service" data-testid="contact-form-service" value={form.service} onChange={set("service")} className={inputCls}>
                    {SERVICE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className={labelCls}>Ihre Nachricht *</label>
                  <textarea id="contact-message" data-testid="contact-form-message" required minLength={5} rows={6} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} placeholder="Beschreiben Sie kurz Ihr Objekt und Ihr Anliegen..." />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#3B62A5] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#2A477A] disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />}
                {sending ? "Wird gesendet..." : "Anfrage senden"}
              </button>
              <p className="mt-4 text-xs text-slate-400">Ihre Daten werden ausschliesslich zur Bearbeitung Ihrer Anfrage verwendet.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
